/**
 * useModuleRegistry Hook
 *
 * @version 1.0
 * @module core/module-registry
 *
 * @description
 * Hook para consumir el registro de módulos de forma reactiva.
 * Re-renderiza cuando se registran/eliminan módulos.
 */

import { useSyncExternalStore, useCallback } from 'react';
import { moduleRegistry } from './moduleRegistry';
import type { ModuleId } from '../module-store/types';
import type { ModuleDefinition } from './types';

/**
 * Hook para obtener un módulo específico del registro
 *
 * @param moduleId - ID del módulo a obtener
 * @returns Definición del módulo o undefined
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   const wasteModule = useModule('waste');
 *
 *   if (!wasteModule) {
 *     return <div>Módulo no disponible</div>;
 *   }
 *
 *   const { leftPanel: Panel } = wasteModule;
 *   return <Panel />;
 * }
 * ```
 */
export function useModule(moduleId: ModuleId | null): ModuleDefinition | undefined {
  // Función para obtener el snapshot del estado
  const getSnapshot = useCallback(() => {
    if (!moduleId) return undefined;
    return moduleRegistry.get(moduleId);
  }, [moduleId]);

  // Función para suscribirse a cambios
  const subscribe = useCallback((callback: () => void) => {
    return moduleRegistry.subscribe(callback);
  }, []);

  // Usar useSyncExternalStore para suscripción reactiva
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Hook para obtener todos los módulos registrados
 *
 * @returns Array con todas las definiciones de módulos
 *
 * @example
 * ```typescript
 * function ModuleList() {
 *   const modules = useAllModules();
 *
 *   return (
 *     <ul>
 *       {modules.map(m => <li key={m.id}>{m.id}</li>)}
 *     </ul>
 *   );
 * }
 * ```
 */
export function useAllModules(): ModuleDefinition[] {
  const getSnapshot = useCallback(() => {
    return moduleRegistry.getAll();
  }, []);

  const subscribe = useCallback((callback: () => void) => {
    return moduleRegistry.subscribe(callback);
  }, []);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Hook para verificar si un módulo está registrado
 *
 * @param moduleId - ID del módulo
 * @returns true si el módulo está registrado
 *
 * @example
 * ```typescript
 * function FeatureGate({ moduleId, children }) {
 *   const isAvailable = useIsModuleRegistered(moduleId);
 *
 *   if (!isAvailable) {
 *     return null;
 *   }
 *
 *   return children;
 * }
 * ```
 */
export function useIsModuleRegistered(moduleId: ModuleId): boolean {
  const getSnapshot = useCallback(() => {
    return moduleRegistry.has(moduleId);
  }, [moduleId]);

  const subscribe = useCallback((callback: () => void) => {
    return moduleRegistry.subscribe(callback);
  }, []);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Hook para obtener los IDs de módulos registrados
 *
 * @returns Array de IDs
 */
export function useRegisteredModuleIds(): ModuleId[] {
  const getSnapshot = useCallback(() => {
    return moduleRegistry.getRegisteredIds();
  }, []);

  const subscribe = useCallback((callback: () => void) => {
    return moduleRegistry.subscribe(callback);
  }, []);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
